import React, { useState, useEffect, useCallback, useMemo } from "react";
import { graphql } from "gatsby";
import { useLocation } from '@reach/router'
import { imgCDN, notBlank, imgSize } from '~/utils/helpers'
import { useTheme } from '~/contexts/ThemeContext'
import { useCart } from '~/contexts/CartContext'
import useAnalytics from '~/utils/analytics'
import useVintInventoryHook from '~/utils/vint-inventory'

import { Seo } from '~/components/shared/seo'
import BreadCrumb from '~/components/pdp/breadcrumb'
import ImgGrid from '~/components/shared/img-grid'

import * as styles from "~/styles/pdp/vintage_pdp.module.scss"
import { aria_btn } from "~/styles/app.module.scss"


export default function ProductTemplate({ data: { vintProduct } }) {
  const product = vintProduct

  const { title, shop_id, handle, media, available, first_full_price, 
  first_product_price, size, year, vdescription, 
  condition, details } = product

  const { setPdpVariant, setQuick, setFreeze } = useTheme()
  const { cartApi, cart } = useCart()
  const { get_prodinv } = useVintInventoryHook()
  const { changeCart } = cartApi
  const { ana_pdp } = useAnalytics()

  const variants = useMemo(() => {
    const prod_vars = product.variants || []
    prod_vars.forEach(x => x['sku'] = `${product.sku}${x.title}`)
    return prod_vars
  }, [product.variants,product.sku])

  const checkOneSize = (title) => {
    const is_os = title.toLowerCase() === 'one size' || title.toLowerCase() === 'os'
    return is_os
  }

  const handleVariantSelect = (id) => {
    const curr_size = variants.find( x => x.id === parseInt(id) )
    if (!curr_size) return
    curr_size['prod_shop_id'] = product.shop_id
    setPdpVariant(curr_size);
    window.history.replaceState({}, null, `/products/${product.handle}?variant=${id}`)
    setPdpVariant(curr_size)
  }

  useEffect(() => {
    setFreeze(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  useEffect(() => {
    const url = new URL(window.location.href);
    const variantId = url.searchParams.get('variant');

    let curr_size = false

    if (variantId) {
      handleVariantSelect(variantId)
    }

    if (variants.length === 1 && checkOneSize(variants[0].title)) {
      curr_size = variants[0]
      handleVariantSelect(curr_size.id)
    }

    ana_pdp(product, curr_size)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants])

  const fprice = (fpp, ffp) => {
    return ffp > fpp ? ffp : fpp
  }

  const init_price = fprice(first_product_price, first_full_price) 
  
  const [pdpInv, setPdpInv] = useState(false);
  const [price, setPrice] = useState(init_price);
  const [sold, setSold] = useState(init_price === 0 || !available)
  
  const fcart = ( cta_var ) => {
    setQuick(false) 
    setFreeze(false)
    changeCart(cta_var, {}, false, product, product.variants[0])
  }

  const checkInv = useCallback( async () => { 
    const inv = await get_prodinv(shop_id)
    setPdpInv(inv)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const checkCart = useCallback((ncart) => { 
    const in_cart = ncart.lines.edges.find(x => x.node.merchandise.id === `gid://shopify/ProductVariant/${variants[0].id}`) || false
    return in_cart
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[variants])


  useEffect(() => {
    checkInv()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInv])

  useEffect(() => {
    if (!pdpInv) return
    
    if ( cart && checkCart(cart) ) {
      setSold(true)
      return
    }

    const newprice = fprice(pdpInv.first_product_price , pdpInv.first_full_price)
    setPrice(newprice)
    setSold(newprice === 0 || !pdpInv.available)

  }, [price, available, pdpInv, cart, checkCart]);

  useEffect(() => {
    if (!cart) return

    const check = checkCart(cart)
    if (check) setSold(true)

  }, [cart, checkCart])



  return (
    <div className={styles.vpdp_wr}>

      <BreadCrumb vintage={true} />

      <div className={styles.vpdp}>
        {(media && media.length > 0) && (
          <div className={styles.images}>
            <ImgGrid 
              media={media} 
              handle={handle} 
              title={title} 
              wr_class={''} 
              linked={false} 
              tile_ind={0} 
              toggleFlats={'pdp'} 
              vintage={true} 
            />
          </div>
        )}
        
        <div className={styles.info}>
          <div className={styles.title}>
            {title.split(' - ')[0]}
          </div>

          <div className={styles.price}>
            {sold ? 'SOLD' : `$${price}`}
          </div>

          {notBlank(vdescription) && (
            <div className={styles.desc} dangerouslySetInnerHTML={{ __html: vdescription}} />
          )}

          <div className={styles.cta}>
            {sold ? (
              <button type="button" className={`${aria_btn} ${styles.disable}`} disabled="disabled">
                Sold 
              </button>
            ) : (
              <button 
                type="button"
                className={`${aria_btn}`}
                onClick={(e) => fcart(variants[0].id)}
              >
                Add to Cart 
              </button>
            )}
          </div>

          <div className={styles.details}>
            {notBlank(year) && (
              <>
                <div className={styles.label}>
                  Year
                </div>
                <div className={styles.data} dangerouslySetInnerHTML={{ __html: year}} />
              </>
            )}
            {notBlank(details) && (
              <>
                <div className={styles.label}>
                  Details
                </div>
                <div className={styles.data} dangerouslySetInnerHTML={{ __html: details}} />
              </>
            )}
            {notBlank(condition) && (
              <>
                <div className={styles.label}>
                  Condition
                </div>
                <div className={styles.data} dangerouslySetInnerHTML={{ __html: condition}} />
              </>
            )}
            {notBlank(size) && (
              <>
                <div className={styles.label}>
                  Size
                </div>
                <div className={styles.data} dangerouslySetInnerHTML={{ __html: size}} />
              </>
            )}
          </div>

          <div className={styles.ship}>
            All sales final. Free US Shipping over $150 unless otherwise noted.
          </div>
        </div>
      </div>

      <div className={styles.vinsp}>
        <div className={styles.vinsp_image}>
          <img 
            src={imgSize("https://cdn.shopify.com/s/files/1/0123/5065/2473/files/Vintage_Care.jpg?v=1681344604",900)}
            className={styles.vinsp_img} 
            alt="Clothing that was an inspiration for our vintage collections"
          />
        </div>

        <div className={styles.vinsp_conts}>
          <div className={styles.vinsp_cont}>
            <div className={styles.title}>
              Provenance and Care
            </div>
            <div className={styles.copy}>
              Our vintage collection consists of items that have remained part of the tapestry of our collective history 
              and culture. Naturally, these items have all had previous lives. There may be signs of wear and patina 
              from the natural aging process. All clothing in our collection has been cleaned, serviced, and repaired 
              and is ready for its new home.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}




export const Head = ({ data: { vintProduct } }) => {
  const product = vintProduct
  const location = useLocation();

  const fabric_tag = (product.filters && product.filters.fabric && product.filters.fabric[0]) || null
  let material = fabric_tag ? fabric_tag.replace('filter-fabric:','') : ''
  if (material.toLowerCase().includes('slub') || material.toLowerCase().includes('pima')) {
    material = material + ' Cotton'
  }

  let desc = ''
  if (product.is_vintage) desc = product.vdescription 
  if (!product.is_vintage && product.handle.includes('gift')) desc = 'Buck Mason Gift Card'
  if (!product.is_vintage && !product.handle.includes('gift') && product.short_description) {
    desc = product.short_description.replace(/<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g, '')
  }

  const variants = product.variants || []
  let sku = ''
  let avail = true

  let image = product.image
  if (!image && product.media.length > 0) {
    image = product.media.find(x => x.type === 'IMAGE') ? product.media.find(x => x.type === 'IMAGE').src : null
  } 
  image = image ? imgCDN(image) : ''

  if (variants.length > 0) {
    let variant = variants.find(x => x.available) ? variants.find(x => x.available) : variants[0]
    sku = variant.sku 
    avail = variant.available
    if (location.search.includes('variant')) {
      const url = new URL(location.href);
      const variantId = url.searchParams.get('variant');
      const url_variant = product.variants.find(x => x.id === parseInt(variantId))
      if (url_variant) {
        sku = url_variant.sku
        avail = url_variant.available
      }
    }
  }

  const color = product.color ? `${product.color.group} ${product.color.name}` : ''

  let reviews = ''
  if (product.okendo_count > 0) {
    reviews = `,"aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "${product.okendo_avg}",
      "ratingCount": "${product.okendo_count}"
    }`
  }

  return (
    <Seo 
      title={product.title}
      description={product.vdescription ? product.vdescription : `${product.title} - ${product.short_description}`}
      image={image}
      url={`/products/${product.handle}`}
    >
      <script id="prod_struc_data" type="application/ld+json">
        {`{
          "@context": "http://schema.org/",
          "@type": "Product",
          "name": "${product.title}",
          "url": "${location.href}",
          "image": "${image}",
          "color": "${color}",
          "material": "${material}",
          "description": "${desc}",
          "mpn": "${product.id}",
          "sku": "${sku}",
          "brand": {
            "@type": "Brand",
            "name": "Buck Mason"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": "${product.first_product_price}",
            "priceValidUntil": "${new Date().getFullYear() + 1}-12-31",
            "availability": "${avail ? 'InStock' : 'OutOfStock'}",
            "seller": {
              "@type": "Organization",
              "name": "Buck Mason"
            }
          }
          ${reviews}
        }`}
      </script>
      <meta property="product:price:amount" content={product.first_product_price} />
      <meta property="product:price:currency" content="USD" />
      <meta property="og:brand" content="Buck Mason" />
      <meta property="og:price:amount" content={product.first_product_price} />
      <meta property="og:price:currency" content="USD" />
    </Seo>
  )
}


export const pageQuery = graphql`
  query ($handle: String!) {
    vintProduct(handle: { eq: $handle }) {
      mid
      available
      category
      gender
      first_full_price
      first_product_price
      handle
      image
      media

      shop_id
      sku
      title
      variants

      is_vintage
      vcoll_title
      vcoll_handle
      size
      year
      vdescription
      condition
      details
    }
  }
`;






